// MembersPage.js
import React, { useState, useEffect, useCallback } from 'react';
import Header from './Header';
import Footer from './Footer';
import PageHero from './PageHero';
import { getAtletas } from '../services/atletasService';
import { getAsociadosPublic } from '../services/clubsService';
import ContactoAsociado from './ContactoAsociado';
import Paginacion from './Paginacion';
import { getAllCMSImages } from '../services/s3Service';
import { getFirstCachedImage } from '../utils/cmsCache';

const ATLETAS_POR_PAGINA = 24;

const Members = () => {
  const [activeView, setActiveView] = useState('athletes');
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('todas');
  const [tipoAsociado, setTipoAsociado] = useState('todos');
  const [heroImage, setHeroImage] = useState(() => getFirstCachedImage(['page_hero_members']));

  const [atletas, setAtletas] = useState([]);
  // Paginación de atletas (la hace el backend: se piden solo los de la página actual)
  const [pagina, setPagina] = useState(1);
  const [totalAtletas, setTotalAtletas] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  // La búsqueda espera a que se deje de escribir (350 ms) para no consultar en cada tecla
  const [busqueda, setBusqueda] = useState('');
  useEffect(() => {
    const nueva = searchTerm.trim();
    if (nueva === busqueda) return undefined;
    const t = setTimeout(() => {
      setBusqueda(nueva);
      setPagina(1); // nueva búsqueda → primera página (ambos cambios en una sola consulta)
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm, busqueda]);
  const cambiarRegion = (region) => {
    setFilterRegion(region);
    setPagina(1);
  };
  const [asociados, setAsociados] = useState([]);
  const [cargandoAtletas, setCargandoAtletas] = useState(true);
  const [cargandoAsociados, setCargandoAsociados] = useState(true);
  const [errorAtletas, setErrorAtletas] = useState(null);
  const [errorAsociados, setErrorAsociados] = useState(null);

  useEffect(() => {
    getAllCMSImages().then((imgs) => setHeroImage(imgs.page_hero_members || null))
      .catch(() => setHeroImage(null));
  }, []);

  /* Paleta ACHIPO (bandera Chile: rojo, azul, blanco) */
  const colors = {
    primary: '#1a1a1a',
    secondary: '#666',
    accent: '#D52B1E',
    accentLight: '#b91c1c',
    lightGray: '#f8f9fa',
    mediumGray: '#eaeaea',
    darkGray: '#333',
    white: '#ffffff',
    blueLight: '#e8f4ff',
    blue: '#0039A6',
    green: '#00aa55'
  };

  const regiones = [
    'todas',
    'Arica y Parinacota',
    'Tarapacá',
    'Antofagasta',
    'Atacama',
    'Coquimbo',
    'Valparaíso',
    'Metropolitana',
    "O'Higgins",
    'Maule',
    'Ñuble',
    'Biobío',
    'Araucanía',
    'Los Ríos',
    'Los Lagos',
    'Aysén',
    'Magallanes'
  ];

  const normalizarRegion = (texto) => {
    if (!texto || typeof texto !== 'string') return '';
    return texto
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\u0301/g, '')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  };

  const fetchAtletas = useCallback(() => {
    let cancelado = false;
    setCargandoAtletas(true);
    setErrorAtletas(null);
    const regionParam = filterRegion === 'todas' ? '' : filterRegion;
    getAtletas({ search: busqueda, region: regionParam, page: pagina, limit: ATLETAS_POR_PAGINA })
      .then(({ items, total, totalPages }) => {
        if (cancelado) return;
        setAtletas(items);
        setTotalAtletas(total ?? items.length);
        setTotalPaginas(totalPages ?? 1);
      })
      .catch((err) => { if (!cancelado) setErrorAtletas(err?.message || 'Error al cargar atletas'); })
      .finally(() => { if (!cancelado) setCargandoAtletas(false); });
    return () => { cancelado = true; };
  }, [busqueda, filterRegion, pagina]);

  useEffect(() => fetchAtletas(), [fetchAtletas]);

  const cambiarPagina = (n) => {
    setPagina(n);
    const lista = document.getElementById('lista-miembros');
    if (lista) window.scrollTo({ top: lista.getBoundingClientRect().top + window.scrollY - 120, behavior: 'smooth' });
  };

  useEffect(() => {
    let cancelled = false;
    setCargandoAsociados(true);
    setErrorAsociados(null);
    getAsociadosPublic()
      .then((lista) => { if (!cancelled) setAsociados(lista); })
      .catch((err) => { if (!cancelled) setErrorAsociados(err?.message || 'Error al cargar asociados'); })
      .finally(() => { if (!cancelled) setCargandoAsociados(false); });
    return () => { cancelled = true; };
  }, []);

  const asociadosFiltrados = asociados.filter((a) => {
    if (tipoAsociado !== 'todos' && a.tipo !== tipoAsociado) return false;
    if (filterRegion !== 'todas') {
      const rNorm = normalizarRegion(filterRegion);
      const aNorm = normalizarRegion(a.region);
      if (!aNorm || rNorm !== aNorm) return false;
    }
    return true;
  });

  const handleAthleteClick = (athlete) => setSelectedAthlete(athlete);
  const handleBackToList = () => setSelectedAthlete(null);

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterRegion('todas');
    setPagina(1);
    setTipoAsociado('todos');
  };

  return (
    <div style={{ 
      width: '100%', 
      margin: '0', 
      backgroundColor: colors.white, 
      minHeight: '100vh',
      color: colors.primary
    }}>
      <Header />
      <PageHero
        eyebrow="Comunidad"
        imageUrl={heroImage}
        title="Comunidad"
        subtitle="Atletas, clubs y gimnasios de la federación"
      />
      <div className="page-content-wrap" style={{ paddingTop: '40px', paddingBottom: '40px', minHeight: '70vh' }}>
        {/* Filtros de búsqueda */}
        <div style={{ 
          marginBottom: '30px',
          marginTop: '-2cm',
          backgroundColor: colors.lightGray,
          padding: '20px',
          borderRadius: '6px',
          border: `1px solid ${colors.mediumGray}`
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '15px'
          }}>
            <h3 style={{ 
              color: colors.primary, 
              fontSize: '1.2em',
              margin: 0,
              fontWeight: '600'
            }}>
              Buscar y Filtrar
            </h3>
            <button
              type="button"
              onClick={handleClearFilters}
              className="btn btn-outline btn-sm"
            >
              Limpiar Filtros
            </button>
          </div>

          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px',
            marginBottom: '15px'
          }}>
            {/* Buscador (solo atletas: nombre o apellido) */}
            {activeView === 'athletes' && (
              <div>
                <label style={{ 
                  display: 'block', 
                  color: colors.secondary,
                  fontSize: '0.9em',
                  marginBottom: '6px',
                  fontWeight: '500'
                }}>
                  Buscar por nombre o apellido
                </label>
                <input
                  type="text"
                  placeholder="Nombre o apellido..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '4px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.blue}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                />
              </div>
            )}

            {/* Filtro por Región (como Asociados) */}
            <div>
              <label style={{ 
                display: 'block', 
                color: colors.secondary,
                fontSize: '0.9em',
                marginBottom: '6px',
                fontWeight: '500'
              }}>
                Región
              </label>
              <select
                value={filterRegion}
                onChange={(e) => cambiarRegion(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.mediumGray}`,
                  borderRadius: '4px',
                  fontSize: '0.95em',
                  color: colors.primary,
                  backgroundColor: colors.white,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = colors.blue}
                onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
              >
                {regiones.map((r) => (
                  <option key={r} value={r}>
                    {r === 'todas' ? 'Todas las regiones' : r}
                  </option>
                ))}
              </select>
            </div>

            {/* Tipo Club/Gimnasio (solo en vista asociados) */}
            {activeView === 'asociados' && (
              <div>
                <label style={{ 
                  display: 'block', 
                  color: colors.secondary,
                  fontSize: '0.9em',
                  marginBottom: '6px',
                  fontWeight: '500'
                }}>
                  Tipo
                </label>
                <select
                  value={tipoAsociado}
                  onChange={(e) => setTipoAsociado(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '4px',
                    fontSize: '0.95em',
                    color: colors.primary,
                    backgroundColor: colors.white,
                    cursor: 'pointer'
                  }}
                >
                  <option value="todos">Todos</option>
                  <option value="gimnasio">Gimnasio</option>
                  <option value="club">Club</option>
                </select>
              </div>
            )}
          </div>

          {/* Contador de resultados */}
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: colors.secondary,
            fontSize: '0.9em',
            paddingTop: '15px',
            borderTop: `1px solid ${colors.mediumGray}`
          }}>
            <div>
              Mostrando 
              <span style={{ 
                color: colors.accent, 
                fontWeight: '600',
                margin: '0 5px'
              }}>
                {activeView === 'athletes' ? totalAtletas : asociadosFiltrados.length}
              </span>
              {activeView === 'athletes' ? ' atletas' : ' asociados'}
            </div>
            {(searchTerm || filterRegion !== 'todas' || tipoAsociado !== 'todos') && (
              <div style={{ 
                backgroundColor: colors.accent + '15',
                padding: '5px 10px',
                borderRadius: '4px',
                color: colors.accent
              }}>
                Filtros activos
              </div>
            )}
          </div>
        </div>

        {/* Navegación minimalista */}
        <div style={{ 
          display: 'flex', 
          gap: '10px',
          marginBottom: '30px',
          borderBottom: `1px solid ${colors.mediumGray}`,
          paddingBottom: '15px',
          flexWrap: 'wrap'
        }}>
          <button
            type="button"
            onClick={() => setActiveView('athletes')}
            className={`btn ${activeView === 'athletes' ? 'btn-secondary' : 'btn-ghost'}`}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            Atletas ({totalAtletas})
          </button>
          <button
            type="button"
            onClick={() => setActiveView('asociados')}
            className={`btn ${activeView === 'asociados' ? 'btn-secondary' : 'btn-ghost'}`}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            Clubs y Gimnasios ({asociados.length})
          </button>
        </div>

        {cargandoAtletas && activeView === 'athletes' && atletas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.secondary }}>
            <p style={{ fontSize: '1.1em' }}>Cargando atletas...</p>
          </div>
        ) : errorAtletas && activeView === 'athletes' ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.secondary }}>
            <p style={{ fontSize: '1.1em' }}>{errorAtletas}</p>
          </div>
        ) : cargandoAsociados && activeView === 'asociados' ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.secondary }}>
            <p style={{ fontSize: '1.1em' }}>Cargando clubs y gimnasios...</p>
          </div>
        ) : errorAsociados && activeView === 'asociados' ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.secondary }}>
            <p style={{ fontSize: '1.1em' }}>{errorAsociados}</p>
          </div>
        ) : (activeView === 'athletes' && atletas.length === 0) || (activeView === 'asociados' && asociadosFiltrados.length === 0) ? (
          <div style={{ 
            textAlign: 'center',
            padding: '50px 20px',
            backgroundColor: colors.lightGray,
            borderRadius: '6px',
            border: `1px solid ${colors.mediumGray}`
          }}>
            <div style={{ marginBottom: '20px' }} />
            <h3 style={{ color: colors.primary, fontSize: '1.3em', marginBottom: '10px' }}>
              No se encontraron resultados
            </h3>
            <p style={{ color: colors.secondary, maxWidth: '400px', margin: '0 auto', lineHeight: '1.5' }}>
              No hay {activeView === 'athletes' ? 'atletas' : 'asociados'} que coincidan con los filtros.
            </p>
            <button
              type="button"
              onClick={handleClearFilters}
              className="btn btn-secondary"
              style={{ marginTop: '20px' }}
            >
              Limpiar filtros
            </button>
          </div>
        ) : (
          <>
            {/* Vista de Atletas (desde BD con total) */}
            {activeView === 'athletes' && !selectedAthlete && (
              <>
              <div
                id="lista-miembros"
                aria-busy={cargandoAtletas}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: '20px', opacity: cargandoAtletas ? 0.5 : 1, transition: 'opacity .15s' }}
              >
                {atletas.map((athlete) => (
                  <div
                    key={athlete.rut}
                    style={{
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '6px',
                      padding: '20px',
                      backgroundColor: colors.white,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleAthleteClick(athlete)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                      e.currentTarget.style.borderColor = colors.blue;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = colors.mediumGray;
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: (athlete.genero === 'F') ? '#ffe8f4' : colors.blueLight,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px',
                        fontSize: '1.5em'
                      }}>
                        {athlete.genero === 'F' ? 'F' : 'M'}
                      </div>
                      <h4 style={{ color: colors.primary, fontSize: '1.1em', margin: 0, fontWeight: '600' }}>
                        {athlete.nombre_completo}
                      </h4>
                    </div>
                    <div style={{ backgroundColor: colors.lightGray, padding: '12px', borderRadius: '4px', marginBottom: '15px', fontSize: '0.9em' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ color: colors.secondary }}>Club</span>
                        <span style={{ color: colors.primary, fontWeight: '500' }}>{athlete.club || '—'}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ color: colors.secondary, fontSize: '0.85em' }}>Total</div>
                        <div style={{ color: colors.blue, fontWeight: '600', fontSize: '1.1em' }}>
                          {athlete.total != null ? `${athlete.total} kg` : '—'}
                        </div>
                      </div>
                      <div style={{ fontSize: '0.85em', color: colors.secondary, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>{[athlete.ciudad, athlete.region].filter(Boolean).join(', ') || '—'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Paginacion
                pagina={pagina}
                totalPaginas={totalPaginas}
                total={totalAtletas}
                porPagina={ATLETAS_POR_PAGINA}
                onCambiar={cambiarPagina}
                deshabilitado={cargandoAtletas}
                etiqueta="atletas"
              />
              </>
            )}

            {/* Vista Clubs y Gimnasios (igual que Asociados) */}
            {activeView === 'asociados' && asociadosFiltrados.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: '20px' }}>
                {asociadosFiltrados.map((asociado) => (
                  <div
                    key={asociado.id}
                    style={{
                      backgroundColor: colors.white,
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '8px',
                      padding: '25px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ marginBottom: '15px' }}>
                      <h3 style={{ fontSize: '1.3em', fontWeight: '600', color: colors.primary, margin: 0, paddingRight: '70px', overflowWrap: 'anywhere' }}>
                        {asociado.nombre}
                      </h3>
                    </div>
                    <span style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      backgroundColor: asociado.tipo === 'gimnasio' ? '#0039A6' : '#D52B1E',
                      color: colors.white,
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.75em',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      zIndex: 1
                    }}>
                      {asociado.tipo === 'gimnasio' ? 'Gimnasio' : 'Club'}
                    </span>
                    <div style={{ marginBottom: '15px' }}>
                      <p style={{ color: colors.secondary, fontSize: '0.9em', margin: '5px 0' }}>
                        <strong>Ubicación:</strong> {[asociado.ciudad, asociado.region].filter(Boolean).join(', ') || 'Sin ubicación'}
                      </p>
                      <ContactoAsociado asociado={asociado} conEtiquetas />
                    </div>
                    {(asociado.servicios && asociado.servicios.length > 0) && (
                      <div style={{ borderTop: `1px solid ${colors.mediumGray}`, paddingTop: '15px' }}>
                        <p style={{ fontSize: '0.85em', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Servicios:</p>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {(asociado.servicios || []).map((s, i) => (
                            <li key={i} style={{ color: colors.secondary, fontSize: '0.85em', marginBottom: '5px', paddingLeft: '15px', position: 'relative' }}>
                              <span style={{ position: 'absolute', left: 0, color: colors.blue }}>•</span>
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Detalle de Atleta */}
        {selectedAthlete && (
          <div style={{ 
            backgroundColor: colors.white,
            borderRadius: '6px',
            padding: '25px',
            border: `1px solid ${colors.mediumGray}`
          }}>
            <button 
              type="button"
              onClick={handleBackToList}
              className="btn btn-link"
              style={{ padding: '8px 0', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <span>←</span>
              Volver al listado
            </button>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '70px',
                height: '70px',
                backgroundColor: (selectedAthlete.genero === 'F') ? '#ffe8f4' : colors.blueLight,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '15px',
                fontSize: '2em'
              }}>
                {selectedAthlete.genero === 'F' ? 'F' : 'M'}
              </div>
              <div>
                <h3 style={{ color: colors.primary, fontSize: '1.5em', margin: '0 0 8px 0', fontWeight: '600' }}>
                  {selectedAthlete.nombre_completo}
                </h3>
                <p style={{ color: colors.secondary, margin: 0 }}>
                  {[selectedAthlete.ciudad, selectedAthlete.region].filter(Boolean).join(', ') || '—'}
                </p>
              </div>
            </div>

            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '15px',
              marginBottom: '20px'
            }}>
              <div style={{ backgroundColor: colors.lightGray, padding: '12px', borderRadius: '4px' }}>
                <div style={{ color: colors.secondary, fontSize: '0.85em', marginBottom: '4px' }}>Club</div>
                <div style={{ color: colors.primary, fontWeight: '500' }}>{selectedAthlete.club || '—'}</div>
              </div>
              <div style={{ backgroundColor: colors.lightGray, padding: '12px', borderRadius: '4px' }}>
                <div style={{ color: colors.secondary, fontSize: '0.85em', marginBottom: '4px' }}>Total</div>
                <div style={{ color: colors.blue, fontWeight: '600', fontSize: '1.2em' }}>
                  {selectedAthlete.total != null ? `${selectedAthlete.total} kg` : '—'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enlaces de navegación */}
        <div style={{ 
          marginTop: '40px',
          padding: '20px',
          backgroundColor: colors.lightGray,
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            flexWrap: 'wrap'
          }}>
            <a href="/events" className="btn btn-secondary">
              Eventos
            </a>
            <a href="/records" className="btn btn-secondary">
              Récords
            </a>
            <a href="/about" className="btn btn-outline">
              Sobre Nosotros
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Members;